import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { components } from '@invo.space/shared'

import { DocumentFormValues } from '../DocumentForm'

type TaxRate = components['schemas']['TaxRate']

export interface TaxSelectProps<
  TFieldValues extends FieldValues = DocumentFormValues,
> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  value?: number | null
  onChange?: (value: number | null) => void
  placeholder?: string
}

export const TaxSelect = <
  TFieldValues extends FieldValues = DocumentFormValues,
>({
  control,
  name,
  value: externalValue,
  onChange: externalOnChange,
  placeholder,
}: TaxSelectProps<TFieldValues>) => {
  const { t } = useTranslation()
  const selectPlaceholder =
    placeholder !== undefined ? placeholder : t('taxSelect.placeholder')
  const {
    data: taxRates,
    isLoading,
  } = useSWR<Collection<TaxRate>>('/api/v2/tax-rates', apiFetcher)

  if (isLoading) {
    return <Loading />
  }

  // If used with external value/onChange (standalone mode - without FormField wrapper)
  if (externalValue !== undefined || externalOnChange) {
    return (
      <Select
        value={externalValue?.toString() || ''}
        onValueChange={(val) => {
          const numValue = val ? parseFloat(val) : null
          externalOnChange?.(numValue)
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={selectPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {taxRates?.data.map((tax) => (
            <SelectItem key={tax.percentage} value={tax.percentage.toString()}>
              {tax.percentage}%
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  // If used with FormField (original mode)
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <Select
            value={field.value?.toString() || ''}
            onValueChange={(val) => {
              const numValue = val ? parseFloat(val) : null
              field.onChange(numValue)
            }}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={selectPlaceholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {!isLoading &&
                taxRates?.data.map((tax) => (
                  <SelectItem
                    key={tax.percentage}
                    value={tax.percentage.toString()}
                  >
                    {tax.percentage}%
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
